import { useState, useRef, useEffect } from "react";
import { Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

export default function MessageThread({ requestId, isAdmin = false, customerName }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const endRef = useRef(null);

  const scrollToEnd = () => {
    requestAnimationFrame(() => endRef.current?.scrollIntoView({ behavior: "smooth" }));
  };

  const load = async () => {
    try {
      const url = isAdmin
        ? `/admin/requests/${requestId}`
        : `/requests/${requestId}/messages`;
      const { data } = await api.get(url);
      const next = isAdmin ? data.messages || [] : data;
      setMessages(next);
    } catch (err) {
      // silent — the page will show its own errors
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!requestId) return;
    load();
    const t = setInterval(load, 7000);
    return () => clearInterval(t);
     
  }, [requestId, isAdmin]);

  useEffect(scrollToEnd, [messages.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      const url = isAdmin
        ? `/admin/requests/${requestId}/messages`
        : `/requests/${requestId}/messages`;
      const body = isAdmin ? { text } : { text, sender_name: customerName };
      const { data } = await api.post(url, body);
      setMessages((m) => [...m, data]);
      setText("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="bg-[#18181E] border border-white/10 rounded-2xl overflow-hidden flex flex-col"
      data-testid="message-thread"
    >
      <div className="p-5 border-b border-white/5">
        <h4 className="font-display text-sm font-bold uppercase tracking-wider text-amber-400">
          Messages
        </h4>
        <p className="text-xs text-slate-500 mt-1">
          {isAdmin
            ? "Direct line with the customer. They see every reply here."
            : "Chat directly with the GETEASY CAR team."}
        </p>
      </div>

      <div
        className="flex-1 max-h-[380px] overflow-y-auto p-5 space-y-3"
        data-testid="message-list"
      >
        {loading && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-4 h-4 animate-spin text-amber-400" />
          </div>
        )}
        {!loading && messages.length === 0 && (
          <div className="text-center text-sm text-slate-500 py-6">
            No messages yet. {isAdmin ? "Say hello to your customer." : "Start the conversation below."}
          </div>
        )}
        {messages.map((m) => {
          const mine = isAdmin ? m.sender === "admin" : m.sender === "customer";
          return (
            <div
              key={m.id}
              className={`flex ${mine ? "justify-end" : "justify-start"}`}
              data-testid={`message-${m.sender}`}
            >
              <div
                className={`max-w-[82%] rounded-2xl px-4 py-3 border text-sm ${
                  mine
                    ? "bg-amber-500/15 border-amber-500/40 text-amber-50"
                    : "bg-white/5 border-white/10 text-slate-200"
                }`}
              >
                <div className="text-[10px] uppercase tracking-widest font-semibold opacity-70 mb-1">
                  {m.sender_name || m.sender}
                </div>
                <div className="whitespace-pre-wrap leading-relaxed">{m.text}</div>
                <div className="text-[10px] text-slate-500 mt-1.5">
                  {new Date(m.at).toLocaleString()}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      <form onSubmit={send} className="p-3 border-t border-white/10 bg-[#121216] flex gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          data-testid="message-input"
          placeholder={isAdmin ? "Reply to the customer…" : "Type your message…"}
          className="flex-1 bg-[#0A0A0C] border border-white/10 focus:border-amber-500 text-slate-100 placeholder:text-slate-600 rounded-xl px-4 py-2.5 text-sm outline-none"
        />
        <button
          type="submit"
          disabled={sending || !text.trim()}
          data-testid="message-send-btn"
          className="inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-black font-bold px-5 rounded-xl transition"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </form>
    </div>
  );
}
